import { Plus, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  ANCHOR_POINTS,
  MATRIX_USE_LABELS,
  MAX_MATRIX_LINES,
  roundUse,
  type MatrixAnchors,
  type MatrixUse,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { EditableTable, type EditableColumn } from '../../../../components/editable-table/EditableTable';
import { EmptyState } from '../../../../components/system-states/SystemState';
import { useToasts } from '../../../../stores/toasts';
import { MatrixGuide, MatrixKey } from './MatrixGuide';
import { STARTER_QUALITIES } from './matrix-starters';
import { ScorecardPreview } from './ScorecardPreview';
import styles from './builder.module.css';

// JOB-13's scoring matrix (F5.22, JB-11…JB-14): qualities scored 1–5, each with what a 1, a 3
// and a 5 look like, ticked for where it's used. Each round's scorecard is made from the lines
// ticked for it. A "Critical" line blocks moving on when scored under 2 (§L3, DECISIONS-LOG
// "Start R3" Q4).

export interface MatrixRow {
  readonly key: string;
  readonly label: string;
  readonly anchors: MatrixAnchors;
  readonly isCritical: boolean;
  readonly usedIn: readonly MatrixUse[];
}

export function blankMatrixRow(roundCount: number): MatrixRow {
  // A new quality is used in every round by default (06-qol §7 smart defaults).
  const rounds = Array.from({ length: roundCount }, (_, index) => roundUse(index + 1)).filter((use): use is MatrixUse => use !== null);
  return { key: crypto.randomUUID(), label: '', anchors: {}, isCritical: false, usedIn: rounds };
}

/**
 * The empty matrix's one-click start, ticked as "Add quality" ticks a new line: every interview
 * round, not HR review (HR's stars are optional extras on every CV, and these qualities show in
 * an interview more than on paper).
 */
export function starterMatrixRows(roundCount: number): readonly MatrixRow[] {
  return STARTER_QUALITIES.map((quality) => ({ ...blankMatrixRow(roundCount), label: quality.label, anchors: quality.anchors }));
}

export function namedLines(rows: readonly MatrixRow[]): readonly MatrixRow[] {
  return rows.filter((row) => row.label.trim() !== '');
}

const ANCHOR_PLACEHOLDERS: Readonly<Record<(typeof ANCHOR_POINTS)[number], string>> = {
  '1': 'Repeats the same mistake',
  '3': 'Learns with some help',
  '5': 'Learns a new tool alone',
};

interface MatrixTableProps {
  readonly rows: readonly MatrixRow[];
  /** How many interview rounds the job has: one "Used in" tick each. */
  readonly roundCount: number;
  readonly onCommit: (next: readonly MatrixRow[]) => Promise<void>;
  readonly onRowsChange: (next: readonly MatrixRow[]) => void;
  readonly isReadOnly: boolean;
  /** Where interview rounds are added on this screen, said while there are none. */
  readonly noRoundsNote: string;
}

export function MatrixTable({ rows, roundCount, onCommit, onRowsChange, isReadOnly, noRoundsNote }: MatrixTableProps): ReactNode {
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const uses: readonly MatrixUse[] = [
    'hr_review',
    ...Array.from({ length: roundCount }, (_, index) => roundUse(index + 1)).filter((use): use is MatrixUse => use !== null),
  ];

  const columns: readonly EditableColumn<MatrixRow>[] = [
    {
      key: 'label',
      header: 'Quality',
      width: '18%',
      cell: { kind: 'text', placeholder: 'Trainability', maxLength: 120, value: (row) => row.label, update: (row, label) => ({ ...row, label }) },
    },
    ...ANCHOR_POINTS.map(
      (point): EditableColumn<MatrixRow> => ({
        key: `anchor-${point}`,
        header: `A ${point} looks like`,
        cell: {
          kind: 'text',
          placeholder: ANCHOR_PLACEHOLDERS[point],
          maxLength: 200,
          value: (row) => row.anchors[point] ?? '',
          update: (row, text) => ({ ...row, anchors: { ...row.anchors, [point]: text.trim() === '' ? undefined : text } }),
        },
      }),
    ),
    {
      key: 'critical',
      header: 'Critical',
      width: '8%',
      cell: { kind: 'checkbox', value: (row) => row.isCritical, update: (row, isCritical) => ({ ...row, isCritical }) },
    },
    ...uses.map(
      (use): EditableColumn<MatrixRow> => ({
        key: use,
        header: MATRIX_USE_LABELS[use],
        width: '8%',
        cell: {
          kind: 'checkbox',
          value: (row) => row.usedIn.includes(use),
          update: (row, isUsed) => ({ ...row, usedIn: isUsed ? [...row.usedIn, use] : row.usedIn.filter((candidate) => candidate !== use) }),
        },
      }),
    ),
  ];

  const addRow = (): void => onRowsChange([...rows, blankMatrixRow(roundCount)]);
  const named = namedLines(rows);
  // A round with no line ticked has no scorecard: its column is marked until one is (JB-14).
  const flags = Object.fromEntries(
    unmarkedRounds(named, roundCount).map((round) => [`data-flag-round-${round}`, true]),
  );

  return (
    <div className={styles.tableSection}>
      <MatrixGuide />
      <div className={styles.tableTools}>
        <MatrixKey roundCount={roundCount} noRoundsNote={noRoundsNote} />
        {named.length > 0 && <ScorecardPreview lines={named} noRoundsNote={noRoundsNote} />}
      </div>
      {rows.length === 0 && !isReadOnly ? (
        <EmptyState
          isInset
          title="No qualities yet"
          body="Start with Trainability, Communication, Problem solving and Attitude, each with what a 1, a 3 and a 5 look like, then change anything you like. Or add your own."
          action={
            <>
              <Button variant="solid" size="sm" icon={Sparkles} onClick={() => void onCommit(starterMatrixRows(roundCount)).catch(() => undefined)}>
                Start with common qualities
              </Button>
              <Button variant="ghost" size="sm" icon={Plus} onClick={addRow}>
                Add your own
              </Button>
            </>
          }
        />
      ) : (
        <div className={styles.matrixTable} {...flags}>
          <EditableTable
            label="Scoring matrix"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.key}
            rowLabel={(row) => row.label || 'new quality'}
            isReadOnly={isReadOnly}
            emptyText="No qualities yet. Add what interviewers should score."
            addLabel="Add quality"
            isAddDisabled={rows.length >= MAX_MATRIX_LINES}
            onAdd={addRow}
            onCommit={(change) => onCommit(rows.map((row) => (row.key === change.rowKey ? change.row : row)))}
            onMove={(from, to) => {
              const next = [...rows];
              const [moved] = next.splice(from, 1);
              if (!moved) return;
              next.splice(to, 0, moved);
              void onCommit(next).catch(() => undefined);
            }}
            onDelete={(rowKey) => {
              const index = rows.findIndex((row) => row.key === rowKey);
              const removed = rows[index];
              if (!removed) return;
              const next = rows.filter((row) => row.key !== rowKey);
              void onCommit(next).catch(() => undefined);
              showUndo(`${removed.label || 'Quality'} removed`, () => {
                const restored = [...next];
                restored.splice(index, 0, removed);
                void onCommit(restored).catch(() => undefined);
              });
            }}
          />
        </div>
      )}
    </div>
  );
}

/** The rounds no named line is ticked for, while there are lines at all (`jobSectionStates`). */
export function unmarkedRounds(named: readonly MatrixRow[], roundCount: number): readonly number[] {
  if (named.length === 0) return [];
  return Array.from({ length: roundCount }, (_, index) => index + 1).filter((round) => {
    const use = roundUse(round);
    return use !== null && !named.some((row) => row.usedIn.includes(use));
  });
}
